<template>
  <div class="question">
    <div v-if="hasCheckboxAnswers" v-html="questionTitle" class="question-subtitle"></div>
    <form method="get" @submit.stop.prevent="handleSubmit" :class="{'checkboxes-question': hasCheckboxAnswers}">
      <div class="question-wrap">
        <div v-if="!hasCheckboxAnswers" class="question-title" v-html="questionTitle"></div>
        <div v-if="question.note || question.details">
          <div v-if="question.note" class="question-note" v-html="question.note"></div>

          <ul v-if="question.details && !hasCheckboxAnswers" class="chevron-list blue" v-html="questionDetailsList"></ul>

          <div v-if="hasCheckboxAnswers" class="clearfix">
            <ul v-for="(group, groupKey) in checkboxAnswerGroups" :key="groupKey" class="checkbox-list">
              <li v-for="(answer, answerKey) in group" :key="answerKey">
                <input :id="`section${section}-question${questionKey}-answer${checkboxValue(checkboxAnswerGroups, groupKey, answerKey)}`"
                       type="checkbox"
                       class="styled-checkbox" name="answers[]"
                       :value="checkboxValue(checkboxAnswerGroups, groupKey, answerKey)"
                       v-model="checkboxAnswers" />
                <label :for="`section${section}-question${questionKey}-answer${checkboxValue(checkboxAnswerGroups, groupKey, answerKey)}`">
                  {{ answer }}
                </label>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="!hasRangeSlider && question.footNote" class="question-foot-note" :class="{'mt-2': hasCheckboxAnswers}" v-html="question.footNote"></div>
      </div>

      <div v-if="hasCheckboxAnswers" class="answer">
          <button type="submit" role="button" class="button"
                  :class="checkboxesAnswerButtonClasses(answer)"
                  :disabled="!isCurrent || postingTriageData"
                  @click="handleCheckboxAnswers(checkboxAnswers)"
          >
            Continue
          </button>
        </div>

        <div v-if="hasButtonAnswers" class="answer">
          <div class="btn-group">
            <button type="submit" role="button"
                    v-for="(answerValue, answerText) in buttonAnswers"
                    :key="answerValue"
                    :class="answerButtonClasses(answerValue)"
                    :name="answerText"
                    :value="answerValue"
                    :disabled="!isCurrent || postingTriageData"
                    @click="handleAnswer(answerValue)"
            >
              {{ answerText }}
            </button>
          </div>
        </div>

      <div v-if="hasRangeSlider" class="range-slider-answer">
        <range-slider
          ref="slider"
          class="slider"
          :min="question.range.min"
          :max="question.range.max"
          :step="question.range.step"
          :prefix="question.range.prefix || null"
          :suffix="question.range.suffix || null"
          :name="`${questionKey}-slider`"
          v-model="answer"
          :disabled="!isCurrent || postingTriageData"
          @change="handleRangeChange"
        />

        <div v-if="question.footNote" class="question-foot-note" v-html="question.footNote"></div>

        <button type="submit"
                role="button"
                class="button"
                :class="rangeButtonClasses()"
                :disabled="!isCurrent || postingTriageData"
                @click="handleRangeChange"
        >
          Continue
        </button>
      </div>

      <div v-if="confirmation" class="question-confirmation">
        <p>{{ confirmation }}</p>
        <button type="submit"
                role="button"
                class="button"
                :class="rangeButtonClasses()"
                @click="handleConfirmation"
        >
          Continue
        </button>
      </div>
      <div style="clear: both"></div>
    </form>
  </div>
</template>

<script>
import {mapActions, mapState} from "vuex"
import RangeSlider from "@/components/Slider/RangeSlider";

export default {
  components: {
    RangeSlider,
  },

  props: {
    section: {
      type: Number,
      required: true,
    },

    questionKey: {
      type: Number,
      required: true,
    },

    question: {
      type: Object,
      required: true,
    },

    isCurrent: {
      type: Boolean,
      required: true,
    }
  },

  computed: {
    ...mapState("survey", [
      "answers",
      "sections",
      "postingTriageData",
    ]),

    questionTitle() {
      return Array.isArray(this.question.title)
        ? window.Arr.toHtml(this.question.title)
        : this.question.title;
    },

    questionDetailsList() {
      return window.Arr.toHtml(this.question.details, "li");
    },

    checkboxAnswerGroups() {
      const answers = JSON.parse(JSON.stringify(this.question.details));

      return [
        answers.splice(0, Math.ceil(answers.length / 2)),
        answers
      ];
    },

    hasButtonAnswers() {
      return !this.hasRangeSlider && !this.hasCheckboxAnswers;
    },

    hasCheckboxAnswers() {
      return this.question.answers === "checkboxes";
    },

    hasRangeSlider() {
      return this.question.answers === "range";
    },

    buttonAnswers() {
      if (this.question.answers && typeof this.question.answers === "object") {
        return this.question.answers;
      }

      return {
        Yes: true,
        No: false,
      };
    },
  },

  data() {
    return {
      answer: null,
      checkboxAnswers: [],
      highlightAnswer: undefined,
      broadcastTimeout: null,
      nextDelay: 400,
      confirmation: '',
      confirmed: false
    };
  },

  watch: {
    checkboxAnswers(answer) {
      this.setCheckboxAnswers(answer);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.answer = this.answers[`section${this.section}`][`question${this.questionKey}`];

      if (this.hasCheckboxAnswers) {
        this.checkboxAnswers = this.answer || [];
      }

      if (this.hasRangeSlider && this.answer === null) {
        this.answer = this.question.range.min;
      }
    });
  },

  beforeDestroy() {
    clearTimeout(this.activeClassTimeout);
  },

  methods: {
    ...mapActions("survey", [
      "setAnswer",
    ]),

    answerButtonClasses(answer) {
      if (this.hasButtonAnswers && Object.keys(this.buttonAnswers).length === 1) {
        return {
          "btn-highlight": this.highlightAnswer === answer,
          "button": true 
        };
      }

      return {
        "btn-active": this.answers[`section${this.section}`][`question${this.questionKey}`] === answer,
        "btn-highlight": this.highlightAnswer === answer,
        "btn": true
      };
    },

    checkboxesAnswerButtonClasses(answer) {
      if (this.answer === null) {
        return {};
      }

      return {
        "btn-highlight": this.highlightAnswer === answer,
      };
    },

    checkboxValue(checkboxAnswerGroups, groupKey, answerKey) {
      let value = 0;

      for (let i = (groupKey - 1); i >= 0; i--) {
        value += checkboxAnswerGroups[groupKey - 1].length;
      }
      
      value += answerKey;

      return value;
    },

    rangeButtonClasses() {
      return {
        "btn-highlight": this.highlightAnswer !== undefined,
      };
    },

    handleConfirmation() {
      this.confirmed = true;
    },

    handleSubmit() {
      clearTimeout(this.broadcastTimeout);

      this.broadcastTimeout = setTimeout(
        () => this.$emit("answer", this.section, this.questionKey, this.question, this.answer, this.confirmed),
        this.nextDelay
      );
    },

    handleAnswer(answer) {
      this.answer = answer;

      if (this.question.confirmation) {
        this.confirmation = this.question.confirmation(answer);
      }

      this.setAnswer({
        section: this.section,
        question: this.questionKey,
        answer
      });

      this.setHighlightedAnswer(answer);
    },

    handleCheckboxAnswers(answer) {
      this.answer = (answer && answer.length) ? answer : false;

      this.setAnswer({
        section: this.section,
        question: this.questionKey,
        answer: this.answer,
      });

      this.setHighlightedAnswer(answer);
    },

    handleRangeChange(e) {
      const answer = this.answer || this.question.range.min;

      this.answer = answer;

      this.setAnswer({
        section: this.section,
        question: this.questionKey,
        answer,
      });

      if (!Number.isInteger(e)) {
        this.setHighlightedAnswer(answer);
      }
    },

    setHighlightedAnswer(answer) {
      clearTimeout(this.activeClassTimeout);

      this.highlightAnswer = answer;

      this.activeClassTimeout = setTimeout(() => this.highlightAnswer = undefined, 1000);
    },

    setCheckboxAnswers(answer) {
      // Don't interfere if the user hasn't clicked continue yet.
      if (this.answer === null) {
        return;
      }

      // Otherwise, set the answer to either an array or false.
      if (!answer || !answer.length) {
        this.answer = false;

        this.setAnswer({
          section: this.section,
          question: this.questionKey,
          answer: false,
        });
      } else {
        this.answer = answer;

        this.setAnswer({
          section: this.section,
          question: this.questionKey,
          answer,
        });
      }
    },
  },
}
</script>

<style lang="scss">
.question {
  min-height: 450px;

  .btn-group {
    float: none;

    @include screen(md) {
      float: right;
    }

    .btn {
      margin:0 auto;
    }
  }
}

.answer {
  margin-top: 30px;
  margin-bottom: 2rem;

  .button {
    margin-top: 10px;
    float: none;

    @include screen(md) {
      margin-top: 0;
      float: right;
    }
  }
}

.question-confirmation {
  text-align: center;
  margin-bottom: 30px;
  
  @include screen(md) {
    margin-top: 30px;
    float: right;
    max-width: 600px;
    clear: both;
  }

  button {
    @include screen(md) {
      float: right;
    }
  }
}
</style>
